import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { etatDesLieuxApi } from '../services/api';

const EtatsDesLieux = () => {
  const navigate = useNavigate();
  const [etats, setEtats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ type: '', status: '' });

  useEffect(() => {
    fetchEtats();
  }, []);

  const fetchEtats = async () => {
    try {
      setLoading(true);
      const response = await etatDesLieuxApi.getAll();
      setEtats(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des états des lieux');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet état des lieux ?')) {
      return;
    }

    try {
      await etatDesLieuxApi.delete(id);
      toast.success('État des lieux supprimé avec succès');
      fetchEtats();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  const handleDownload = async (id) => {
    try {
      await etatDesLieuxApi.download(id);
      toast.success('Téléchargement en cours...');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleGeneratePDF = async (id) => {
    try {
      await etatDesLieuxApi.generatePDF(id);
      toast.success('PDF généré avec succès');
    } catch (error) {
      toast.error('Erreur lors de la génération du PDF');
    }
  };

  const getStatusBadge = (etat) => {
    const statusConfig = {
      draft: { color: 'bg-gray-100 text-gray-800', label: 'Brouillon' },
      pending_signature: { color: 'bg-yellow-100 text-yellow-800', label: 'En attente de signature' },
      signed: { color: 'bg-green-100 text-green-800', label: 'Signé' },
    };

    const config = statusConfig[etat.status] || statusConfig.draft;

    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    const isEntree = type === 'entrée';
    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
        isEntree ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
      }`}>
        {isEntree ? 'Entrée' : 'Sortie'}
      </span>
    );
  };

  const filteredEtats = etats.filter(etat => {
    if (filter.type && etat.type !== filter.type) return false;
    if (filter.status && etat.status !== filter.status) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-600">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">États des lieux</h1>
        <Link
          to="/etat-des-lieux/new"
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          + Nouvel état des lieux
        </Link>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Filtrer par type
            </label>
            <select
              value={filter.type}
              onChange={(e) => setFilter({ ...filter, type: e.target.value })}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="">Tous les types</option>
              <option value="entrée">Entrée</option>
              <option value="sortie">Sortie</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Filtrer par statut
            </label>
            <select
              value={filter.status}
              onChange={(e) => setFilter({ ...filter, status: e.target.value })}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="">Tous les statuts</option>
              <option value="draft">Brouillon</option>
              <option value="pending_signature">En attente de signature</option>
              <option value="signed">Signé</option>
            </select>
          </div>

          {(filter.type || filter.status) && (
            <div className="flex items-end">
              <button
                onClick={() => setFilter({ type: '', status: '' })}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                Réinitialiser les filtres
              </button>
            </div>
          )}
        </div>
      </div>

      {/* List */}
      {filteredEtats.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-500 mb-4">Aucun état des lieux trouvé</p>
          <Link
            to="/etat-des-lieux/new"
            className="inline-block px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Créer le premier état des lieux
          </Link>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Bien
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Locataire
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredEtats.map((etat) => (
                <tr key={etat.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getTypeBadge(etat.type)}
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">
                      {etat.property?.name || 'N/A'}
                    </div>
                    <div className="text-sm text-gray-500">
                      {etat.property?.address}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">
                      {etat.tenant?.firstName} {etat.tenant?.lastName}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(etat.inspectionDate).toLocaleDateString('fr-FR')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(etat)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => navigate(`/etat-des-lieux/${etat.id}`)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Voir"
                      >
                        Voir
                      </button>

                      {etat.status === 'draft' && (
                        <button
                          onClick={() => navigate(`/etat-des-lieux/${etat.id}/edit`)}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="Modifier"
                        >
                          Modifier
                        </button>
                      )}

                      {etat.status !== 'signed' && (
                        <button
                          onClick={() => navigate(`/etat-des-lieux/${etat.id}/sign`)}
                          className="text-green-600 hover:text-green-900"
                          title="Signer"
                        >
                          Signer
                        </button>
                      )}

                      <button
                        onClick={() => handleDownload(etat.id)}
                        className="text-purple-600 hover:text-purple-900"
                        title="Télécharger PDF"
                      >
                        PDF
                      </button>

                      {(etat.status === 'draft' || !etat.landlordSigned && !etat.tenantSigned) && (
                        <button
                          onClick={() => handleDelete(etat.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Supprimer"
                        >
                          Supprimer
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default EtatsDesLieux;
